import { useState } from "react";
import { Loader2, CheckCircle, XCircle, Trash2, RotateCcw, Download, Eye } from "lucide-react";
import { ContractorActivityDialog } from "./ContractorActivityDialog";
import { zoneLabel } from "@/i18n/zones";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

export const ContractorsTab = (p) => {
  const [activityContractor, setActivityContractor] = useState(null);
  return (
  <div className="space-y-4">
    <ContractorActivityDialog contractor={activityContractor} onClose={() => setActivityContractor(null)} API={p.API} />
    <div className="flex flex-wrap justify-end gap-2">
      <Button variant="outline" size="sm" onClick={() => p.handleExportContractors("csv")} className="text-slate-600" data-testid="export-contractors-csv-button">
        <Download className="w-4 h-4 mr-2" />Export CSV
      </Button>
      <Button variant="outline" size="sm" onClick={() => p.handleExportContractors("excel")} className="text-slate-600" data-testid="export-contractors-excel-button">
        <Download className="w-4 h-4 mr-2" />Export Excel
      </Button>
      <Button variant={p.showContractorTrash ? "default" : "outline"} size="sm" onClick={p.toggleContractorTrashView} className={p.showContractorTrash ? "bg-slate-800 text-white" : "text-slate-600"} data-testid="toggle-contractor-trash-button">
        <Trash2 className="w-4 h-4 mr-2" />Corbeille ({p.contractorTrash.length})
      </Button>
    </div>

    {p.showContractorTrash ? (
      p.contractorTrash.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 text-center py-20" data-testid="contractor-trash-empty">
          <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">La corbeille est vide</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="contractor-trash-table">
          <p className="px-4 py-3 text-sm text-slate-600 bg-slate-50 border-b border-slate-200">
            Les entrepreneurs sont conservés <strong>30 jours</strong> dans la corbeille puis supprimés définitivement.
          </p>
          {p.selectedContractors.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-orange-50 border-b border-orange-200" data-testid="contractor-trash-bulk-bar">
              <span className="text-sm font-semibold text-slate-900">{p.selectedContractors.length} compte(s) sélectionné(s)</span>
              <Button size="sm" variant="outline" onClick={() => p.handleRestoreContractors(p.selectedContractors)} className="bg-white text-green-700 border-green-200 hover:bg-green-50" data-testid="bulk-restore-contractors-button">
                <RotateCcw className="w-4 h-4 mr-1" />Restaurer
              </Button>
              <Button size="sm" variant="outline" onClick={() => p.handlePurgeContractors(p.selectedContractors)} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-purge-contractors-button">
                <Trash2 className="w-4 h-4 mr-1" />Supprimer définitivement
              </Button>
              <button onClick={() => p.setSelectedContractors([])} className="text-sm text-slate-500 hover:text-slate-700 ml-auto" data-testid="contractor-trash-clear-selection">Tout désélectionner</button>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={p.contractorTrash.length > 0 && p.selectedContractors.length === p.contractorTrash.length} onCheckedChange={p.toggleContractorSelectAll} data-testid="select-all-contractor-trash" /></TableHead>
                <TableHead>Entreprise</TableHead><TableHead>Email</TableHead><TableHead>TVA</TableHead>
                <TableHead>Supprimé le</TableHead><TableHead>Jours restants</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {p.contractorTrash.map((c) => (
                <TableRow key={c.id} data-testid={`contractor-trash-row-${c.id}`} className={p.selectedContractors.includes(c.id) ? "bg-orange-50/50" : ""}>
                  <TableCell><Checkbox checked={p.selectedContractors.includes(c.id)} onCheckedChange={() => p.toggleContractorSelection(c.id)} data-testid={`select-contractor-trash-${c.id}`} /></TableCell>
                  <TableCell className="font-medium text-slate-900">{c.company_name}</TableCell>
                  <TableCell className="text-slate-600">{c.email}</TableCell>
                  <TableCell className="text-slate-600">{c.vat_number}</TableCell>
                  <TableCell className="text-slate-600">{new Date(c.deleted_at).toLocaleDateString("fr-BE")}</TableCell>
                  <TableCell>
                    <span className={`badge ${c.days_left <= 7 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`} data-testid={`contractor-days-left-${c.id}`}>{c.days_left} j</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => p.handleRestoreContractors([c.id])} className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`restore-contractor-${c.id}`}><RotateCcw className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => p.handlePurgeContractors([c.id])} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`purge-contractor-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )
    ) : (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="contractors-table">
        {p.loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : p.contractors.length === 0 ? (
          <div className="text-center py-20"><p className="text-slate-500">Aucun entrepreneur inscrit</p></div>
        ) : (
          <>
            {p.selectedContractors.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-orange-50 border-b border-orange-200" data-testid="contractors-bulk-bar">
                <span className="text-sm font-semibold text-slate-900">{p.selectedContractors.length} compte(s) sélectionné(s)</span>
                <Button size="sm" variant="outline" onClick={() => p.handleBulkContractorStatus("active")} className="bg-white text-green-700 border-green-200 hover:bg-green-50" data-testid="bulk-activate-button">
                  <CheckCircle className="w-4 h-4 mr-1" />Activer
                </Button>
                <Button size="sm" variant="outline" onClick={() => p.handleBulkContractorStatus("suspended")} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-suspend-button">
                  <XCircle className="w-4 h-4 mr-1" />Suspendre
                </Button>
                <Button size="sm" variant="outline" onClick={p.handleBulkContractorDelete} className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="bulk-delete-contractors-button">
                  <Trash2 className="w-4 h-4 mr-1" />Supprimer
                </Button>
                <button onClick={() => p.setSelectedContractors([])} className="text-sm text-slate-500 hover:text-slate-700 ml-auto" data-testid="contractors-clear-selection">Tout désélectionner</button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><Checkbox checked={p.contractors.length > 0 && p.selectedContractors.length === p.contractors.length} onCheckedChange={p.toggleContractorSelectAll} data-testid="select-all-contractors" /></TableHead>
                  <TableHead>Entreprise</TableHead><TableHead>Contact</TableHead><TableHead>TVA</TableHead>
                  <TableHead>Zones</TableHead><TableHead>Statut</TableHead><TableHead>Activité</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {p.contractors.map((contractor) => (
                  <TableRow key={contractor.id} data-testid={`contractor-row-${contractor.id}`} className={p.selectedContractors.includes(contractor.id) ? "bg-orange-50/50" : ""}>
                    <TableCell><Checkbox checked={p.selectedContractors.includes(contractor.id)} onCheckedChange={() => p.toggleContractorSelection(contractor.id)} data-testid={`select-contractor-${contractor.id}`} /></TableCell>
                    <TableCell className="font-medium text-slate-900">{contractor.company_name}</TableCell>
                    <TableCell><p className="text-slate-900">{contractor.email}</p><p className="text-sm text-slate-500">{contractor.phone}</p></TableCell>
                    <TableCell className="text-slate-600">{contractor.vat_number}</TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[220px]">{contractor.service_areas?.map((z) => zoneLabel(z)).join(", ") || "-"}</TableCell>
                    <TableCell>
                      {contractor.status === "suspended" ? (
                        <span className="badge bg-red-100 text-red-700" data-testid={`contractor-status-${contractor.id}`}>Suspendu</span>
                      ) : contractor.status === "active" ? (
                        <span className="badge badge-active" data-testid={`contractor-status-${contractor.id}`}>Actif</span>
                      ) : (
                        <span className="badge badge-pending" data-testid={`contractor-status-${contractor.id}`}>En attente</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => setActivityContractor(contractor)} className="text-slate-700" data-testid={`view-contractor-${contractor.id}`}>
                        <Eye className="w-4 h-4 mr-1" />Voir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    )}
  </div>
  );
};
